import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { vacancyApi } from '../../api/recruitment';
import { departmentsApi } from '../../api/admin/departments';
import { positionsApi } from '../../api/admin/positions';
import { locationsApi } from '../../api/admin/locations';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', published: 'Published', applications: 'Accepting Applications', screening: 'Screening',
  longlisting: 'Longlisting', shortlisting: 'Shortlisting', assessment: 'Assessment', interview: 'Interview',
  reference_check: 'Reference Check', selection: 'Selection', approval: 'Awaiting Approval', offer: 'Offer',
  hired: 'Hired', closed: 'Closed', cancelled: 'Cancelled',
};

export function VacanciesPage() {
  const [searchParams] = useSearchParams();
  const fromRequest = searchParams.get('fromRequest');
  const [modalOpen, setModalOpen] = useState(!!fromRequest);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const vacanciesQuery = useQuery({ queryKey: ['vacancies'], queryFn: () => vacancyApi.list() });
  const departmentsQuery = useQuery({ queryKey: ['admin-departments'], queryFn: () => departmentsApi.list() });
  const positionsQuery = useQuery({ queryKey: ['admin-positions'], queryFn: () => positionsApi.list() });
  const locationsQuery = useQuery({ queryKey: ['admin-locations'], queryFn: () => locationsApi.list() });
  const { register, handleSubmit, reset } = useForm<Record<string, string>>();

  useEffect(() => { if (fromRequest) setModalOpen(true); }, [fromRequest]);

  const createMutation = useMutation({
    mutationFn: (values: Record<string, string>) => vacancyApi.create({
      workforceRequestId: fromRequest ? Number(fromRequest) : undefined,
      positionId: values.positionId ? Number(values.positionId) : undefined,
      departmentId: values.departmentId ? Number(values.departmentId) : undefined,
      dutyStationId: values.dutyStationId ? Number(values.dutyStationId) : undefined,
      employmentType: values.employmentType || undefined,
      grade: values.grade || undefined,
      jobDescription: values.jobDescription, requirements: values.requirements, numberOfPositions: Number(values.numberOfPositions) || 1,
      openingDate: values.openingDate || undefined, closingDate: values.closingDate || undefined,
    }),
    onSuccess: (result) => {
      showSuccess(`Vacancy created - ${result.data.vacancyReference}`);
      queryClient.invalidateQueries({ queryKey: ['vacancies'] });
      setModalOpen(false);
      reset();
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create vacancy.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => vacancyApi.delete(id),
    onSuccess: () => { showSuccess('Vacancy deleted.'); queryClient.invalidateQueries({ queryKey: ['vacancies'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to delete vacancy.'),
  });

  const handleDelete = (id: number, reference: string) => {
    if (window.confirm(`Delete ${reference}? This can only succeed if it has no real applicants - otherwise use Cancel instead.`)) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="page">
      <Link to="/hr">&larr; Back to HR Dashboard</Link>
      <div className="page-header">
        <h1>Recruitment - Vacancies</h1>
        <button onClick={() => setModalOpen(true)}>New Vacancy</button>
      </div>

      {vacanciesQuery.data && (
        <DataTable
          rows={vacanciesQuery.data.data}
          rowKey={(v) => v.id}
          emptyMessage="No vacancies yet."
          columns={[
            { header: 'Reference', render: (v) => <Link to={`/hr/vacancies/${v.id}`}>{v.vacancy_reference}</Link> },
            { header: 'Position', render: (v) => v.position_title ?? '-' },
            { header: 'Department', render: (v) => v.department_name ?? '-' },
            { header: 'Status', render: (v) => <StatusBadge status={v.status} label={STATUS_LABELS[v.status]} /> },
            {
              header: '',
              render: (v) => (
                <span className="row-actions">
                  <Link to={`/hr/vacancies/${v.id}`}>Edit</Link>
                  <button className="link-button" onClick={() => handleDelete(v.id, v.vacancy_reference)} disabled={deleteMutation.isPending}>Delete</button>
                </span>
              ),
            },
          ]}
        />
      )}

      {modalOpen && (
        <Modal title={fromRequest ? `New Vacancy (from Workforce Request #${fromRequest})` : 'New Vacancy'} onClose={() => setModalOpen(false)}>
          <p className="muted">{fromRequest ? 'Position, department, duty station, employment type, and grade have been pre-filled from the approved workforce request - adjust if needed.' : 'The vacancy reference is generated automatically.'}</p>
          <form onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
            <label>
              Position
              <select {...register('positionId')}>
                <option value="">- select -</option>
                {positionsQuery.data?.data.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </label>
            <label>
              Department
              <select {...register('departmentId')}>
                <option value="">- select -</option>
                {departmentsQuery.data?.data.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </label>
            <label>
              Duty Station
              <select {...register('dutyStationId')}>
                <option value="">- select -</option>
                {locationsQuery.data?.data.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </label>
            <label>
              Employment Type
              <select {...register('employmentType')}>
                <option value="">- select -</option>
                <option value="full_time">Full-Time</option><option value="part_time">Part-Time</option>
                <option value="consultant">Consultant</option><option value="intern">Intern</option><option value="volunteer">Volunteer</option>
              </select>
            </label>
            <label>Grade<input {...register('grade')} /></label>
            <label>Job Description<textarea rows={3} {...register('jobDescription')} /></label>
            <label>Requirements<textarea rows={3} {...register('requirements')} /></label>
            <label>Number of Positions<input type="number" defaultValue={1} {...register('numberOfPositions')} /></label>
            <label>Opening Date<input type="date" {...register('openingDate')} /></label>
            <label>Closing Date (application deadline)<input type="date" {...register('closingDate')} /></label>
            <div className="form-actions"><button type="submit" className="btn-primary" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Create'}</button><button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
